import type { ContentItem } from "./types";

export type GuideFaq = {
  question: string;
  answer: string;
};

export type GuideEnhancement = {
  metaTitle: string;
  metaDescription: string;
  quickAnswer: string;
  faqs: readonly GuideFaq[];
};

const guideEnhancements: Readonly<Record<string, GuideEnhancement>> = {
  "hello-world": {
    metaTitle: "Aquarium Troubleshooting Without Wasted Time or Money",
    metaDescription:
      "Troubleshoot aquarium problems with practical guides and focused expert help before trial-and-error costs more time, energy, and money.",
    quickAnswer:
      "Aquarium troubleshooting often gets expensive when conflicting advice leads to repeated purchases and changes. Start by recording the symptom, recent changes, tank size, livestock, filtration, and current water readings. Use the relevant guide to narrow the likely causes, then verify before acting. When the situation is unclear or the stakes are high, a focused video consultation can turn those details into a prioritized plan.",
    faqs: [
      {
        question: "When is a consultation more useful than another online answer?",
        answer:
          "A consultation is most useful when several causes fit the same symptom, the advice you found conflicts, or a wrong change could make the problem worse. Seeing the tank and its readings helps an experienced professional prioritize what to verify and what to do next.",
      },
      {
        question: "What should I collect before asking for aquarium help?",
        answer:
          "Collect your tank size, livestock list, filter type, current ammonia, nitrite, and nitrate readings, recent maintenance, and any food, treatment, livestock, or equipment changes. Photos or a video view of the full system also make the conversation more useful.",
      },
    ],
  },
  "why-celebrities-and-billionaires-trust-us": {
    metaTitle: "Why Aquarium Owners Trust Experienced Help",
    metaDescription:
      "See why owners of valuable, complex aquariums rely on experience—and how to get focused guidance without paying for an on-site service call.",
    quickAnswer:
      "Owners of large or complex aquariums rely on experienced help because small mistakes can quickly become expensive. Aquarium Helpline makes Brian Hoover's field experience available in a focused 15-minute video consultation. The value is not celebrity treatment; it is having an experienced professional see the system, review the available readings, separate likely causes from distractions, and recommend the next useful action without an equipment upsell.",
    faqs: [
      {
        question: "What can an aquarium expert assess over video?",
        answer:
          "A video call can show the tank, filtration, plumbing, water movement, livestock behavior, and visible symptoms while you share recent readings and changes. That context can narrow the likely causes and identify the next useful check, though some problems still require an on-site technician or veterinarian.",
      },
      {
        question: "Will Aquarium Helpline try to sell me equipment?",
        answer:
          "Aquarium Helpline is positioned as advice first, with no equipment upsell. The goal of the consultation is to review the system and give you a practical order of operations, including when the best next step is testing, maintenance, local service, or veterinary care.",
      },
    ],
  },
  "stop-aquarium-leaks-fast": {
    metaTitle: "How to Find and Stop Aquarium Leaks Fast",
    metaDescription:
      "Learn the common sources of aquarium leaks, how to narrow the cause, and when a focused video consultation can prevent costly trial and error.",
    quickAnswer:
      "An aquarium leak may come from plumbing connections, filters, pumps, the tank itself, or a recent service mistake. Identify where the water first appears, note what equipment is running, and avoid random adjustments that can hide the source. Because leaks can damage property and threaten livestock, move quickly and get experienced eyes on the system when the origin or safe response is not obvious.",
    faqs: [
      {
        question: "Where do aquarium leaks commonly start?",
        answer:
          "Common sources include plumbing connections, filters, pumps, poorly built tanks, and components disturbed during servicing. Water can travel before it becomes visible, so the wettest visible spot is not always the original source.",
      },
      {
        question: "Can a video consultation replace emergency on-site help?",
        answer:
          "No. A video consultation can help you inspect the system, narrow the source, and plan a response, but it is not emergency property remediation, electrical service, or an on-site aquarium repair. Prioritize immediate local help when water, electricity, structural damage, or livestock safety creates an urgent risk.",
      },
    ],
  },
  "why-is-my-fish-tank-green": {
    metaTitle: "Why Is My Fish Tank Green? Causes and Next Steps",
    metaDescription:
      "Green aquarium water often points to excess light or nutrients. Check lighting, feeding, filtration, and maintenance before choosing a fix.",
    quickAnswer:
      "Green aquarium water is usually an algae bloom supported by excess light, excess nutrients, inadequate filtration, or infrequent water changes. Check how long the lights run, whether sunlight reaches the tank, how much food enters the system, how the filter is performing, and what the water tests show. Correct the underlying imbalance gradually instead of stacking treatments without confirming the likely cause.",
    faqs: [
      {
        question: "What should I check first when aquarium water turns green?",
        answer:
          "Start with light exposure, feeding, filtration, recent water changes, and current water readings. Note any recent change in livestock, food, maintenance, or equipment. Those details help distinguish a temporary algae bloom from a broader water-quality or filtration problem.",
      },
      {
        question: "Why not use an algae treatment immediately?",
        answer:
          "A treatment may address visible algae without correcting the light, nutrient, filtration, or maintenance imbalance that allowed it to grow. Verify the likely cause first, make measured changes, and ask for tank-specific help when the readings or symptoms do not fit the usual pattern.",
      },
    ],
  },
  "why-are-my-fishs-eyes-cloudy": {
    metaTitle: "Why Are My Fish's Eyes Cloudy? Common Causes",
    metaDescription:
      "Cloudy fish eyes can relate to water quality, injury, infection, parasites, or nutrition. Learn what to check and when to seek veterinary care.",
    quickAnswer:
      "Cloudy eyes in fish can be associated with poor water quality, physical injury, bacterial or fungal infection, parasites, or inadequate nutrition. Start with ammonia, nitrite, nitrate, temperature, and recent changes, then look for other symptoms such as sores, spots, lethargy, or appetite loss. Do not choose medication from one symptom alone; accurate identification matters, and urgent or worsening cases need an aquatic veterinarian.",
    faqs: [
      {
        question: "What water readings matter when a fish has cloudy eyes?",
        answer:
          "Check ammonia, nitrite, nitrate, temperature, and the parameters appropriate for the species. Also record recent water changes, filter maintenance, new livestock, treatments, and whether one or several fish are affected. The pattern is often as important as a single reading.",
      },
      {
        question: "Should I medicate a fish with cloudy eyes?",
        answer:
          "Do not select medication from cloudiness alone. Injury, water quality, infection, parasites, and nutrition can call for different responses. An aquarium consultation can help organize the evidence, but diagnosis and treatment of an ill animal should come from a qualified aquatic veterinarian.",
      },
    ],
  },
  "why-is-my-fish-not-eating": {
    metaTitle: "Why Is My Fish Not Eating? Causes to Check",
    metaDescription:
      "A fish may stop eating because of water quality, temperature, stress, setup, or illness. Use this checklist to narrow the cause safely.",
    quickAnswer:
      "A fish may stop eating because of ammonia or nitrite, unstable temperature or pH, low oxygen, overcrowding, inadequate shelter, disturbance, competition, or illness. Test the water and compare current conditions with the species' needs before changing food or adding treatment. Observe whether one fish or the whole tank is affected and note any other symptoms; worsening health concerns belong with an aquatic veterinarian.",
    faqs: [
      {
        question: "What should I check first when a fish stops eating?",
        answer:
          "Test the water, confirm temperature stability, check filtration and water movement, and review recent additions or maintenance. Watch whether the fish is hiding, breathing differently, being bullied, or showing visible symptoms. Also confirm that the food and feeding method suit the species.",
      },
      {
        question: "When does appetite loss need professional help?",
        answer:
          "Get qualified help when appetite loss persists, affects multiple fish, follows a water-quality event, or appears with breathing changes, sores, swelling, spots, or unusual behavior. Aquarium Helpline can review the system, while an aquatic veterinarian should diagnose and treat an ill animal.",
      },
    ],
  },
  "what-is-the-nitrogen-cycle": {
    metaTitle: "Aquarium Nitrogen Cycle: A Practical Guide",
    metaDescription:
      "Understand how aquarium bacteria process ammonia into nitrite and nitrate, how to test a cycle, and why patient stocking protects fish.",
    quickAnswer:
      "The aquarium nitrogen cycle is the biological process in which beneficial bacteria convert toxic ammonia from waste into nitrite and then nitrate. A new system is considered cycled only when testing shows that it can process its ammonia source without leaving detectable ammonia or nitrite. Establishing that bacterial population takes time, so test regularly, avoid rushing livestock additions, and make decisions from readings rather than a fixed calendar.",
    faqs: [
      {
        question: "How do I know when an aquarium is cycled?",
        answer:
          "Use reliable tests rather than elapsed time alone. A cycled system can process its ammonia source while ammonia and nitrite return to undetectable levels and nitrate is present or otherwise managed by the system. Continue testing as livestock is added gradually.",
      },
      {
        question: "What can slow or disrupt the nitrogen cycle?",
        answer:
          "Temperature, pH, the available bacterial habitat, the ammonia source, stocking changes, and filter or substrate disruption can affect the process. If readings stall or move unexpectedly, review the complete setup before adding more livestock or making several changes at once.",
      },
    ],
  },
  "why-a-turtle-is-the-perfect-pet-your-complete-guide-to-turtle-ownership": {
    metaTitle: "Turtle Ownership Guide: Care, Setup, and Commitment",
    metaDescription:
      "Explore turtle lifespan, habitat, lighting, filtration, diet, costs, and long-term care before deciding whether a turtle fits your home.",
    quickAnswer:
      "Turtles can be engaging, long-lived pets, but they are not a low-effort commitment. The right setup depends on the species and typically requires suitable land and water areas, heat, appropriate lighting, strong filtration, a balanced diet, regular cleaning, and access to qualified veterinary care. Research adult size, lifespan, legal requirements, and ongoing habitat costs before bringing a turtle home.",
    faqs: [
      {
        question: "Are turtles really low-maintenance pets?",
        answer:
          "Turtles may need less daily interaction than some pets, but their habitat requires consistent heating, lighting, filtration, cleaning, feeding, and water-quality care. Many species also live for decades and grow well beyond the size seen in pet stores.",
      },
      {
        question: "What should I research before choosing a turtle?",
        answer:
          "Research the exact species' adult size, lifespan, diet, temperature, lighting, land and water needs, social behavior, local ownership rules, and veterinary access. Plan the adult enclosure and long-term care budget, not just the starter setup.",
      },
    ],
  },
  "paludariums-the-art-of-creating-living-worlds-where-land-meets-water": {
    metaTitle: "Paludariums: Designing a Land-and-Water World",
    metaDescription:
      "Learn how paludariums combine land, water, plants, and animals—and what to plan before building a stable semi-aquatic ecosystem.",
    quickAnswer:
      "A paludarium combines aquatic and terrestrial zones in one living display. Successful builds begin with the intended plants and animals, then work backward through enclosure size, stable landforms, drainage, filtration, humidity, lighting, access, and maintenance. The artistic layout matters, but biological compatibility and safe construction come first. Start with a manageable system, allow it to mature, and change one variable at a time.",
    faqs: [
      {
        question: "What is the difference between a paludarium and an aquarium?",
        answer:
          "An aquarium is primarily an aquatic system. A paludarium deliberately combines water and land areas, often with both aquatic and terrestrial plants and carefully selected animals. That mix adds drainage, humidity, access, and compatibility requirements beyond a typical aquarium.",
      },
      {
        question: "What should I choose first in a paludarium build?",
        answer:
          "Choose the type of environment and its intended inhabitants before selecting equipment or building the hardscape. Their adult size, temperature, humidity, water depth, escape risk, and compatibility determine the enclosure and life-support requirements.",
      },
    ],
  },
  "aquascaping-the-meditative-art-of-underwater-landscape-architecture": {
    metaTitle: "Aquascaping: Styles, Setup, and Maintenance",
    metaDescription:
      "Explore Iwagumi, Nature Aquarium, and Dutch aquascaping styles, plus the plant care, water chemistry, tools, and patience each layout needs.",
    quickAnswer:
      "Aquascaping is the design and cultivation of a living underwater landscape. Styles such as Iwagumi, Nature Aquarium, and Dutch layouts use different combinations of stone, wood, and plants, but all depend on compatible species, deliberate composition, stable water chemistry, appropriate light, nutrients, and regular pruning. Choose a style that matches your maintenance capacity, plan for mature plant growth, and expect the composition to evolve over time.",
    faqs: [
      {
        question: "Which aquascaping style is best for a beginner?",
        answer:
          "The best starting style is one that fits your plant knowledge, equipment, time, and willingness to prune. Study the restraint of Iwagumi, the landscape approach of Nature Aquarium, and the plant-focused Dutch style, then simplify your first layout rather than mixing every technique.",
      },
      {
        question: "Why does an aquascape need ongoing maintenance?",
        answer:
          "Plants grow, compete, and change the composition. Regular pruning, water testing, nutrient and light adjustments, filter care, and algae control keep the system healthy and preserve the intended layout. Planning for mature growth makes that routine easier.",
      },
    ],
  },
};

const fallbackFaqs: readonly GuideFaq[] = [
  {
    question: "Can a general aquarium guide account for my exact setup?",
    answer:
      "A guide can organize the common possibilities, but it cannot see your livestock, equipment, maintenance history, recent changes, or current water readings. A focused video consultation adds that context and helps prioritize the safest next useful action.",
  },
  {
    question: "What should I prepare for an aquarium consultation?",
    answer:
      "Bring the tank size, livestock list, filter type, current water readings, recent maintenance, and any recent food, treatment, livestock, or equipment changes. A clear view of the aquarium and equipment is also useful.",
  },
];

export function guideEnhancementFor(item: ContentItem): GuideEnhancement {
  return guideEnhancements[item.slug] ?? {
    metaTitle: item.title,
    metaDescription: item.summary,
    quickAnswer: item.summary,
    faqs: fallbackFaqs,
  };
}
